import React from 'react';
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Button,
  List,
  ListItem,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import ReplayIcon from '@mui/icons-material/Replay';

// Per-creator outcome → badge styling. Mirrors the `outcome` values returned
// by POST /bulk-generate/from-report (created | merged | follow_up_created | no_new_content).
const OUTCOME_CONFIG = {
  created:            { label: 'Added',           color: '#15803D', bg: 'rgba(21,128,61,0.08)',  border: 'rgba(21,128,61,0.22)' },
  merged:             { label: 'Added',           color: '#15803D', bg: 'rgba(21,128,61,0.08)',  border: 'rgba(21,128,61,0.22)' },
  follow_up_created:  { label: 'Follow-up queued', color: '#7C6FF7', bg: 'rgba(124,111,247,0.08)', border: 'rgba(124,111,247,0.22)' },
  no_new_content:     { label: 'Already added',    color: '#9CA3AF', bg: '#F9FAFB',                border: '#E5E7EB' },
};

// Summarizes the outcome of attaching a report to a campaign — what was newly
// added, what was already there untouched, and what got queued separately as
// a follow-up (creators who already have a generated payment). Built as a
// dedicated summary instead of a toast because the per-creator breakdown is
// exactly what makes "nothing happened" vs "some things were skipped, on
// purpose" legible to the person clicking Attach.
const AttachResultModal = ({ open, onClose, campaignName, result, onGoToQueue }) => {
  const { queued = 0, duplicate = 0, followUps = 0, results = [] } = result || {};

  const creatorsAdded = results.filter((r) => r.outcome === 'created' || r.outcome === 'merged').length;
  const nothingNew = queued === 0;

  const headline = nothingNew
    ? 'Nothing new to add'
    : `${queued} post${queued === 1 ? '' : 's'} added across ${creatorsAdded} creator${creatorsAdded === 1 ? '' : 's'}`;

  const subline = nothingNew
    ? `All of this report's content is already in "${campaignName}"'s payment queue — nothing was duplicated.`
    : duplicate > 0
      ? `${duplicate} post${duplicate === 1 ? '' : 's'} already in the queue ${duplicate === 1 ? 'was' : 'were'} left untouched.`
      : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: '20px',
          overflow: 'hidden',
          border: '1px solid #E5E7EB',
          boxShadow: '0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)',
        },
      }}
    >
      <Box sx={{ height: 4, background: 'linear-gradient(90deg, #FF5200 0%, #FF8C00 100%)', flexShrink: 0 }} />

      {/* Header */}
      <Box sx={{
        px: 3, pt: 2.5, pb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        borderBottom: '1px solid #F3F4F6', background: '#fff',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Box sx={{
            width: 38, height: 38, borderRadius: '10px', flexShrink: 0,
            background: nothingNew ? 'rgba(156,163,175,0.12)' : 'rgba(21,128,61,0.1)',
            border: `1px solid ${nothingNew ? 'rgba(156,163,175,0.28)' : 'rgba(21,128,61,0.22)'}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {nothingNew
              ? <InfoRoundedIcon sx={{ color: '#9CA3AF', fontSize: 19 }} />
              : <CheckCircleRoundedIcon sx={{ color: '#15803D', fontSize: 19 }} />}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: '#111827', fontWeight: 700, fontSize: 16, lineHeight: 1.25 }}>
              Attached to "{campaignName}"
            </Typography>
            <Typography sx={{ color: '#6B7280', fontSize: 12.5, mt: 0.25 }}>
              {headline}
            </Typography>
          </Box>
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{ color: '#9CA3AF', background: '#F9FAFB', border: '1px solid #E5E7EB', flexShrink: 0, '&:hover': { background: '#F3F4F6', color: '#374151' } }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {subline && (
        <Box sx={{ px: 3, pt: 2 }}>
          <Typography sx={{ color: '#6B7280', fontSize: 12.5 }}>{subline}</Typography>
        </Box>
      )}

      {followUps > 0 && (
        <Box sx={{ px: 3, pt: 1.5 }}>
          <Box sx={{
            display: 'flex', alignItems: 'flex-start', gap: 1, p: 1.25, borderRadius: '10px',
            bgcolor: 'rgba(124,111,247,0.06)', border: '1px solid rgba(124,111,247,0.18)',
          }}>
            <ReplayIcon sx={{ color: '#7C6FF7', fontSize: 16, mt: 0.15 }} />
            <Typography sx={{ color: '#4C4A6E', fontSize: 12 }}>
              {followUps} creator{followUps === 1 ? '' : 's'} already had a generated payment for this campaign — new content was queued separately as a follow-up rather than changing settled data.
            </Typography>
          </Box>
        </Box>
      )}

      {/* Per-creator breakdown */}
      {results.length > 0 && (
        <Box sx={{
          mt: 2, mx: 3, mb: 1, maxHeight: 260, overflowY: 'auto', border: '1px solid #F3F4F6', borderRadius: '12px',
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-thumb': { background: 'rgba(255,82,0,0.25)', borderRadius: 4 },
        }}>
          <List disablePadding>
            {results.map((r, i) => {
              const cfg = OUTCOME_CONFIG[r.outcome] || OUTCOME_CONFIG.no_new_content;
              return (
                <ListItem
                  key={`${r.username}-${i}`}
                  sx={{
                    px: 2, py: 1,
                    borderBottom: i === results.length - 1 ? 'none' : '1px solid #F9FAFB',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1,
                  }}
                >
                  <Typography sx={{ fontSize: 13, color: '#374151', fontWeight: 500, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {/^company\//i.test(r.username || '') ? r.username : `@${r.username}`}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                    {r.added > 0 && (
                      <Typography sx={{ fontSize: 11, color: '#9CA3AF' }}>
                        +{r.added} new{r.duplicate > 0 ? `, ${r.duplicate} existing` : ''}
                      </Typography>
                    )}
                    {r.added === 0 && r.duplicate > 0 && (
                      <Typography sx={{ fontSize: 11, color: '#9CA3AF' }}>
                        {r.duplicate} already queued
                      </Typography>
                    )}
                    <Chip
                      size="small"
                      label={cfg.label}
                      sx={{
                        fontSize: 10.5, fontWeight: 600, height: 22,
                        color: cfg.color, bgcolor: cfg.bg, border: `1px solid ${cfg.border}`,
                      }}
                    />
                  </Box>
                </ListItem>
              );
            })}
          </List>
        </Box>
      )}

      {/* Footer */}
      <Box sx={{ px: 3, py: 2, borderTop: '1px solid #F3F4F6', display: 'flex', gap: 1.5, background: '#FAFAFA' }}>
        <Button
          fullWidth
          onClick={onClose}
          sx={{
            color: '#6B7280', background: '#fff', borderRadius: '10px', py: 1.1, fontSize: 13.5, fontWeight: 500,
            textTransform: 'none', border: '1px solid #E5E7EB', boxShadow: 'none',
            '&:hover': { background: '#F9FAFB', borderColor: '#D1D5DB', color: '#374151' },
          }}
        >
          Stay Here
        </Button>
        <Button
          fullWidth
          variant="contained"
          onClick={onGoToQueue}
          sx={{
            background: 'linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)', borderRadius: '10px', py: 1.1,
            fontSize: 13.5, fontWeight: 600, textTransform: 'none', boxShadow: '0 4px 14px rgba(255,82,0,0.3)',
            '&:hover': { background: 'linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)', opacity: 0.9, boxShadow: '0 6px 20px rgba(255,82,0,0.45)' },
          }}
        >
          Go to Payment Queue
        </Button>
      </Box>
    </Dialog>
  );
};

export default AttachResultModal;
